import { Box, Stack } from '@mui/material';
import {
  PolicyType,
  QuoteDetails,
  SubmissionRenewalType,
  SubmissionType,
  useGetQuotesQuery,
  useQuoteSubscriptionQuery,
} from '@converge/graphql';
import { PropsWithChildren, memo, useEffect, useMemo } from 'react';
import {
  QuoteListActionsContext,
  QuoteListDispatchContext,
  QuoteListStateContext,
  useQuoteListReducer,
} from './quote-list.context';

import { BlockUntilAutoQuoteFinishes } from './block-until-auto-quote-finishes';
import { BlockUntilScoreAndRateFinishes } from '../block-until-score-and-rate-finishes';
import { CircularLoader } from '../../../presentation';
import { ToggleObsoleteQuotesButton } from './toggle-obsolete-quotes.button';
import { UnderwritingQuoteBindModel } from './underwriting.quote.bind.modal';
import { UnderwritingQuoteCreateButton } from './underwriting.quote.create.button';
import { UnderwritingQuoteList } from './underwriting.quote.grid';
import { UnderwritingQuotePackageCreateButton } from './underwriting.quote-package.create.button';
import { UnderwritingQuoteUpdateSubmissionStatusButton } from './underwriting.quote.update-submission-status.button';
import { UnderwritingSortQuoteButton } from './underwriting.quote.sort.button';
import { UnderwritingSubmissionDetailPageSlug } from '@converge/data';
import { UnderwritingSubmissionPageContainer } from '../underwriting.submission.page.container';
import { UnderwritingUnbindSubmission } from './underwriting.unbind-submission.button';
import { config } from '@converge/config';
import { useAuthContext } from '../../auth/require-auth';
import { useUnderwritingSubmissionDetailPage } from '../underwriting.submission.detail.hooks';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';

export const UnderwritingQuotePage = memo(() => {
  const { submission } = useUnderwritingSubmissionStateContext();
  const isAutoQuoteRenewal =
    submission.renewalType === SubmissionRenewalType.AutoQuoteRenewal;
  const {
    params: { submissionUuid },
  } = useUnderwritingSubmissionDetailPage();
  const { refetch } = useGetQuotesQuery({ submissionUuid });

  return (
    <BlockUntilScoreAndRateFinishes>
      {isAutoQuoteRenewal ? (
        <BlockUntilAutoQuoteFinishes onDone={refetch}>
          <UnderwritingQuotePageInner />
        </BlockUntilAutoQuoteFinishes>
      ) : (
        <UnderwritingQuotePageInner />
      )}
    </BlockUntilScoreAndRateFinishes>
  );
});

export const UnderwritingQuotePageInner = memo(
  () => {
    const { isUserIdle } = useAuthContext();
    const {
      navigate,
      params: { submissionUuid },
    } = useUnderwritingSubmissionDetailPage();
    const {
      propertyAndCasualtyLicenseExpired,
      surplusLinesTaxLicenseExpired,
      info: {
        allowCreatingQuotes,
        boundQuote,
        allowUndoQuotePackage,
        submissionStatusIsWrong,
        isMissingBoundQuote,
        allowSortQuotes,
        submission,
        policyTypeName,
      },
    } = useUnderwritingSubmissionStateContext();
    const {
      quotes,
      loading: quotesLoading,
      // startPolling,
      // stopPolling,
      refetch,
    } = useGetQuotesQuery({
      submissionUuid,
    });

    const [state, dispatch, actions] = useQuoteListReducer(quotes ?? []);
    const { updateQuoteEventNotice } = useQuoteSubscriptionQuery();

    useEffect(() => {
      dispatch(actions.setQuotes(quotes ?? []));
    }, [actions, dispatch, quotes]);

    const { obsoleteQuotes, filteredQuotes, quoteToBind } = useMemo(() => {
      return {
        filteredQuotes: state.showObsoleteQuotes
          ? state.quotes
          : state.quotes.filter((quote) => !quote.isObsolete),
        obsoleteQuotes: state.quotes.filter((quote) => quote.isObsolete),
        quoteToBind: state.quoteToBindUuid
          ? state.quotes.find((quote) => quote.uuid === state.quoteToBindUuid)
          : undefined,
      };
    }, [state.quoteToBindUuid, state.quotes, state.showObsoleteQuotes]);

    const shouldPoll = useMemo(() => {
      return (
        !isUserIdle &&
        allowCreatingQuotes &&
        !quotesLoading &&
        filteredQuotes.length < 3
      );
    }, [allowCreatingQuotes, filteredQuotes.length, isUserIdle, quotesLoading]);

    useEffect(() => {
      if (shouldPoll === true && updateQuoteEventNotice) {
        refetch();
      }
    }, [refetch, updateQuoteEventNotice, shouldPoll]);

    return (
      <QuoteListDispatchContext.Provider value={dispatch}>
        <QuoteListStateContext.Provider value={state}>
          <QuoteListActionsContext.Provider value={actions}>
            <UnderwritingSubmissionPageContainer
              title="Quote"
              headerRight={
                obsoleteQuotes.length > 0 && <ToggleObsoleteQuotesButton />
              }
              onNext={
                boundQuote
                  ? () => {
                      navigate.page({
                        sectionUrl: UnderwritingSubmissionDetailPageSlug.Bind,
                      });
                    }
                  : undefined
              }
              footerLeft={
                <Stack flexDirection={'row'}>
                  <UnderwritingQuotePackageCreateButton />

                  {allowUndoQuotePackage && (
                    <UnderwritingQuotePackageCreateButton.UndoButton />
                  )}

                  <UnderwritingQuoteCreateButton
                    show={
                      policyTypeName ===
                        PolicyType.Excess.toLocaleLowerCase() &&
                      quotes.length !== 0
                        ? true
                        : false
                    }
                    diffrentLabel={true}
                  />

                  {config.featureFlags
                    .showUpdateSubmissionStatusButtonOnQuotePage &&
                    submissionStatusIsWrong && (
                      <UnderwritingQuoteUpdateSubmissionStatusButton />
                    )}

                  {isMissingBoundQuote && (
                    <Box style={{ marginLeft: '10px' }}>
                      <UnderwritingUnbindSubmission />
                    </Box>
                  )}

                  {config.featureFlags.showSortQuoteOptionsButton &&
                    allowSortQuotes && <UnderwritingSortQuoteButton />}
                </Stack>
              }
              onPrevious={() => {
                navigate.page({
                  sectionUrl: UnderwritingSubmissionDetailPageSlug.RiskProfile,
                });
              }}
            >
              <Inner
                loading={quotesLoading}
                propertyAndCasualtyLicenseExpired={
                  propertyAndCasualtyLicenseExpired
                }
                surplusLinesTaxLicenseExpired={surplusLinesTaxLicenseExpired}
                onSetBindQuoteDialog={(value) =>
                  dispatch(actions.toggleBindConfirmDialog(value, undefined))
                }
                quoteToBind={quoteToBind}
                submissionUuid={submissionUuid ?? ''}
                submissionType={submission?.type}
                showBindConfirmDialog={state.showBindConfirmDialog}
              />
            </UnderwritingSubmissionPageContainer>
          </QuoteListActionsContext.Provider>
        </QuoteListStateContext.Provider>
      </QuoteListDispatchContext.Provider>
    );
  },
  () => true
);

interface IInnerProps {
  loading: boolean;
  quoteToBind?: QuoteDetails;
  submissionUuid: string;
  submissionType?: SubmissionType | null;
  onSetBindQuoteDialog: (value: boolean) => void;
  showBindConfirmDialog: boolean;
  propertyAndCasualtyLicenseExpired: boolean | null | undefined;
  surplusLinesTaxLicenseExpired: boolean | null | undefined;
}

const Inner = ({
  loading,
  submissionUuid,
  submissionType,
  quoteToBind,
  onSetBindQuoteDialog,
  showBindConfirmDialog,
  propertyAndCasualtyLicenseExpired,
  surplusLinesTaxLicenseExpired,
}: PropsWithChildren<IInnerProps>) => {
  if (loading) {
    return <CircularLoader className="circular-loader-style" />;
  }

  return (
    <>
      <UnderwritingQuoteList
        submissionUuid={submissionUuid ?? ''}
        submissionType={submissionType ?? SubmissionType.Primary}
      />
      {quoteToBind && (
        <UnderwritingQuoteBindModel
          propertyAndCasualtyLicenseExpired={propertyAndCasualtyLicenseExpired}
          surplusLinesTaxLicenseExpired={surplusLinesTaxLicenseExpired}
          showBindDialog={showBindConfirmDialog}
          setBindQuoteDialog={(value) => {
            onSetBindQuoteDialog(value);
          }}
          quote={quoteToBind}
          submissionType={submissionType ?? SubmissionType.Primary}
        />
      )}
    </>
  );
};

export default UnderwritingQuotePage;
